import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import { InvoiceProto } from "@noom/noom-contracts/noom_contracts/billing/bms/invoices/invoice";
import { MoneyProto } from "@noom/noom-contracts/noom_contracts/lib/money";
import {
  getPromoCodeApplicationError,
  mapPromoCodeApplicationProtoToPromoCodeFailureReason,
} from "./promo";
import { PromoCodeErrorType } from "./redux/slices/promoCode";
import { CheckoutOrderEntry } from "./redux/slices/checkoutOrders";
import {
  calculateDiscountPercentage,
  hasPositiveMoneyValue,
  subtractMoneyProtos,
} from "./noomContracts/moneyProto";

export function extractBillingCycles(order: OrderProto): {
  initialBillingCycle?: BillingCyclePreviewProto;
  recurringBillingCycle?: BillingCyclePreviewProto;
} {
  let billingCycles: BillingCyclePreviewProto[] | undefined;
  for (const action of order.executionActions ?? []) {
    if (action.createSubscription?.billingCycles?.length) {
      billingCycles = action.createSubscription.billingCycles;
      break;
    }
    if (action.modifySubscription?.billingCycles?.length) {
      billingCycles = action.modifySubscription.billingCycles;
      break;
    }
    if (action.updatePaymentSchedule?.billingCycles?.length) {
      billingCycles = action.updatePaymentSchedule.billingCycles;
      break;
    }
  }
  if (!billingCycles?.length) {
    throw new Error("Could not find billing cycles in order response");
  }
  if (billingCycles.length > 2) {
    throw new Error(
      `Expected at most 2 billing cycles but received ${billingCycles.length}`
    );
  }
  if (billingCycles.length === 1) {
    return {
      recurringBillingCycle: billingCycles[0],
    };
  }
  return {
    initialBillingCycle: billingCycles[0],
    recurringBillingCycle: billingCycles[1],
  };
}

/**
 * extractBillingCycles for render-time reads of a persisted order: undefined
 * instead of throwing when the order is missing or carries no usable cycles.
 */
export function safeExtractBillingCycles(
  order?: OrderProto
): ReturnType<typeof extractBillingCycles> | undefined {
  if (!order) {
    return undefined;
  }
  try {
    return extractBillingCycles(order);
  } catch {
    return undefined;
  }
}

export type PromoCodeApplicationResult = {
  promoCode: string;
  isSuccessful?: boolean;
  errorState?: {
    promoCodeError: string;
    promoCodeErrorType: PromoCodeErrorType;
  };
};

/**
 * Extracts the promo code and its server-side validation outcome from an
 * order's operation.
 */
export function getPromoCodeApplicationResult(
  order: OrderProto
): PromoCodeApplicationResult | undefined {
  const operation = order.operations?.find(
    (op) => op.purchaseOperation || op.switchOperation
  );
  const promoCodeSource =
    operation?.purchaseOperation ?? operation?.switchOperation;
  const promoCode = promoCodeSource?.promoCode;
  if (!promoCode) {
    return undefined;
  }

  const application = promoCodeSource?.promoCodeApplication;
  const isSuccessful = application?.isSuccessful;

  return {
    promoCode,
    isSuccessful,
    errorState: !isSuccessful
      ? getPromoCodeApplicationError(
          mapPromoCodeApplicationProtoToPromoCodeFailureReason(application)
        )
      : undefined,
  };
}

export function extractInvoice(order: OrderProto): InvoiceProto | undefined {
  for (const action of order.executionActions ?? []) {
    if (action.createInvoice?.invoice) {
      return action.createInvoice.invoice;
    }
  }
  return undefined;
}

export function getRefundAmount(order: OrderProto): MoneyProto | undefined {
  for (const action of order.executionActions ?? []) {
    const creditNoteAmount =
      action.createCreditNote?.creditNote?.lineItems?.[0]?.amount;
    if (
      creditNoteAmount &&
      (creditNoteAmount.units || creditNoteAmount.nanos)
    ) {
      return creditNoteAmount;
    }
  }
  return undefined;
}

/**
 * Returns the invoice that reflects what is due today for an order entry:
 * one-time purchases use the direct invoice; subscriptions prefer the
 * initial (trial/intro) cycle when present, otherwise the recurring cycle.
 */
export function getDueTodayInvoice(
  entry: CheckoutOrderEntry
): InvoiceProto | undefined {
  if (entry.purchaseType === "ONE_TIME_PURCHASE") {
    return entry.invoice;
  }
  return (
    entry.initialBillingCycle?.invoice ?? entry.recurringBillingCycle?.invoice
  );
}

/** Discount amount applied on an invoice (base − amount before tax), if any. */
export function getInvoiceDiscountAmount(
  invoice?: InvoiceProto
): MoneyProto | undefined {
  return subtractMoneyProtos(
    invoice?.totalBaseAmount,
    invoice?.totalAmountBeforeTax
  );
}

export function invoiceHasDiscount(invoice?: InvoiceProto): boolean {
  return hasPositiveMoneyValue(getInvoiceDiscountAmount(invoice));
}

export function getInvoiceDiscountPercentage(invoice?: InvoiceProto): number {
  return calculateDiscountPercentage(
    getInvoiceDiscountAmount(invoice),
    invoice?.totalBaseAmount
  );
}
